// constants.ts
// Global constants for musical notes and tuning - TypeScript version

// Import types from schema
import type { Note, Scale } from '../presets/presetSchema';

// ================================================================================
// BASIC MUSICAL CONSTANTS
// ================================================================================

export const NOTES: readonly Note[] = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;
export const STRING_TUNING_NOTE_INDICES: readonly number[] = [4, 11, 7, 2, 9, 4] as const; // E B G D A E
export const NUM_STRINGS = 6 as const;
export const NUM_FRETS_DISPLAY = 15 as const;
export const BOTTOM_BAR_HEIGHT = 60 as const;

// ================================================================================
// VISUALIZATION MODES
// ================================================================================

export interface CagedVisualizationMode {
  readonly key: string;
  readonly label: string;
}

export const CAGED_VISUALIZATION_MODES = {
   LINK: { key: 'LINK', label: 'Link' }
} as const satisfies Record<string, CagedVisualizationMode>;

// ================================================================================
// SCALE DEGREE DISPLAY MAPPING
// ================================================================================

export type ScaleDegree = 'R' | 'm2' | 'M2' | 'm3' | 'M3' | 'P4' | 'A4' | 'd5' | 'P5' | 'm6' | 'M6' | 'm7' | 'M7';

export const SCALE_DEGREE_DISPLAY_MAP: Record<ScaleDegree, string> = {
  'R':  '1',
  'm2': '\u266D2',
  'M2': '2',
  'm3': '\u266D3',
  'M3': '3',
  'P4': '4',
  'A4': '\u266D5',  // Augmented 4th (tritone)
  'd5': '\u266D5',  // Diminished 5th (tritone)
  'P5': '5',
  'm6': '\u266D6',
  'M6': '6',
  'm7': '\u266D7',
  'M7': '7'
} as const;

// ================================================================================
// CAGED SHAPE TYPES AND CONSTANTS
// ================================================================================

export interface CagedShapeNote {
  readonly string: number; // string index
  readonly fret: number; // fret number
  readonly type: ScaleDegree; // interval type
}

export interface CagedShapeDefinition {
  readonly name: string;
  readonly pattern: readonly CagedShapeNote[];
  readonly rootNoteFretInShape: number;
  readonly rootNoteStringInShape: number;
}

export type CagedShapeKey = 'E' | 'A' | 'G' | 'C' | 'D';

export const CAGED_SHAPES: Record<CagedShapeKey, CagedShapeDefinition> = {
  'E': { 
    name: 'E Major Form', 
    pattern: [ 
      { string: 5, fret: 0, type: 'R' }, 
      { string: 4, fret: 2, type: 'P5' }, 
      { string: 3, fret: 2, type: 'R' }, 
      { string: 2, fret: 1, type: 'M3' }, 
      { string: 1, fret: 0, type: 'P5' }, 
      { string: 0, fret: 0, type: 'R' } 
    ], 
    rootNoteFretInShape: 0, 
    rootNoteStringInShape: 5 
  },
  'A': { 
    name: 'A Major Form', 
    pattern: [ 
      { string: 4, fret: 0, type: 'R' }, 
      { string: 3, fret: 2, type: 'P5' }, 
      { string: 2, fret: 2, type: 'R' }, 
      { string: 1, fret: 2, type: 'M3' }, 
      { string: 0, fret: 0, type: 'P5' } 
    ], 
    rootNoteFretInShape: 0, 
    rootNoteStringInShape: 4 
  },
  'G': { 
    name: 'G Major Form', 
    pattern: [ 
      { string: 5, fret: 3, type: 'R' }, 
      { string: 4, fret: 2, type: 'M3' }, 
      { string: 3, fret: 0, type: 'P5' }, 
      { string: 2, fret: 0, type: 'R' }, 
      { string: 1, fret: 0, type: 'M3' }, 
      { string: 0, fret: 3, type: 'R' } 
    ], 
    rootNoteFretInShape: 3, 
    rootNoteStringInShape: 5 
  },
  'C': { 
    name: 'C Major Form', 
    pattern: [ 
      { string: 4, fret: 3, type: 'R' }, 
      { string: 3, fret: 2, type: 'M3' }, 
      { string: 2, fret: 0, type: 'P5' }, 
      { string: 1, fret: 1, type: 'R' }, 
      { string: 0, fret: 0, type: 'M3' } 
    ], 
    rootNoteFretInShape: 3, 
    rootNoteStringInShape: 4 
  },
  'D': { 
    name: 'D Major Form', 
    pattern: [ 
      { string: 3, fret: 0, type: 'R' }, 
      { string: 2, fret: 2, type: 'P5' }, 
      { string: 1, fret: 3, type: 'R' }, 
      { string: 0, fret: 2, type: 'M3' } 
    ], 
    rootNoteFretInShape: 0, 
    rootNoteStringInShape: 3 
  }
} as const;

export const CAGED_SHAPES_MINOR: Record<CagedShapeKey, CagedShapeDefinition> = {
  'E': { 
    name: 'E Minor Form', 
    pattern: [ 
      { string: 5, fret: 0, type: 'R' }, 
      { string: 4, fret: 2, type: 'P5' }, 
      { string: 3, fret: 2, type: 'R' }, 
      { string: 2, fret: 0, type: 'm3' }, 
      { string: 1, fret: 0, type: 'P5' }, 
      { string: 0, fret: 0, type: 'R' } 
    ], 
    rootNoteFretInShape: 0, 
    rootNoteStringInShape: 5 
  },
  'A': { 
    name: 'A Minor Form', 
    pattern: [ 
      { string: 4, fret: 0, type: 'R' }, 
      { string: 3, fret: 2, type: 'P5' }, 
      { string: 2, fret: 2, type: 'R' }, 
      { string: 1, fret: 1, type: 'm3' }, 
      { string: 0, fret: 0, type: 'P5' } 
    ], 
    rootNoteFretInShape: 0, 
    rootNoteStringInShape: 4 
  },
  'G': { 
    name: 'G Minor Form', 
    pattern: [ 
      { string: 5, fret: 3, type: 'R' }, 
      { string: 4, fret: 1, type: 'm3' }, 
      { string: 3, fret: 0, type: 'P5' }, 
      { string: 2, fret: 0, type: 'R' }, 
      { string: 1, fret: 3, type: 'm3' }, 
      { string: 0, fret: 3, type: 'R' } 
    ], 
    rootNoteFretInShape: 3, 
    rootNoteStringInShape: 5 
  },
  'C': { 
    name: 'C Minor Form', 
    pattern: [ 
      { string: 4, fret: 3, type: 'R' }, 
      { string: 3, fret: 1, type: 'm3' }, 
      { string: 2, fret: 0, type: 'P5' }, 
      { string: 1, fret: 1, type: 'R' }, 
      { string: 0, fret: 3, type: 'm3' } 
    ], 
    rootNoteFretInShape: 3, 
    rootNoteStringInShape: 4 
  },
  'D': { 
    name: 'D Minor Form', 
    pattern: [ 
      { string: 3, fret: 0, type: 'R' }, 
      { string: 2, fret: 2, type: 'P5' }, 
      { string: 1, fret: 3, type: 'R' }, 
      { string: 0, fret: 1, type: 'm3' } 
    ], 
    rootNoteFretInShape: 0, 
    rootNoteStringInShape: 3 
  }
} as const;

// ================================================================================
// SCALE FORMULAS
// ================================================================================

export const SCALE_FORMULAS: Record<Scale, readonly number[]> = {
  // Diatonic Scales
  MAJOR: [0, 2, 4, 5, 7, 9, 11],
  MINOR: [0, 2, 3, 5, 7, 8, 10],
  HARMONIC_MINOR: [0, 2, 3, 5, 7, 8, 11],
  MELODIC_MINOR: [0, 2, 3, 5, 7, 9, 11],
  
  // Modal Scales
  DORIAN: [0, 2, 3, 5, 7, 9, 10],
  PHRYGIAN: [0, 1, 3, 5, 7, 8, 10],
  LYDIAN: [0, 2, 4, 6, 7, 9, 11],
  MIXOLYDIAN: [0, 2, 4, 5, 7, 9, 10],
  LOCRIAN: [0, 1, 3, 5, 6, 8, 10],
  
  // Pentatonic Scales
  PENTATONIC_MAJOR: [0, 2, 4, 7, 9],
  PENTATONIC_MINOR: [0, 3, 5, 7, 10],
  
  // Blues and Other Scales
  BLUES: [0, 3, 5, 6, 7, 10],
  CHROMATIC: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  WHOLE_TONE: [0, 2, 4, 6, 8, 10],
  OCTATONIC_HALF_WHOLE: [0, 1, 3, 4, 6, 7, 9, 10],
  OCTATONIC_WHOLE_HALF: [0, 2, 3, 5, 6, 8, 9, 11],
  
  // Triads
  MAJOR_TRIAD: [0, 4, 7],
  MINOR_TRIAD: [0, 3, 7],
  DIMINISHED_TRIAD: [0, 3, 6],
  AUGMENTED_TRIAD: [0, 4, 8],
  
  // 7th Chords
  DOMINANT_7TH: [0, 4, 7, 10],
  MAJOR_7TH: [0, 4, 7, 11],
  MINOR_7TH: [0, 3, 7, 10],
  MINOR_MAJOR_7TH: [0, 3, 7, 11],
  DIMINISHED_7TH: [0, 3, 6, 9],
  HALF_DIMINISHED_7TH: [0, 3, 6, 10],
  AUGMENTED_7TH: [0, 4, 8, 10],
  AUGMENTED_MAJOR_7TH: [0, 4, 8, 11]
} as const;

export const SCALE_DEGREES: readonly ScaleDegree[] = ['R', 'm2', 'M2', 'm3', 'M3', 'P4', 'd5', 'P5', 'm6', 'M6', 'm7', 'M7'] as const;

// ================================================================================
// NOTE COLORS
// ================================================================================

export interface NoteColor {
  readonly bg: string;
  readonly border: string;
  readonly text: string;
  readonly stroke: string;
}

// Pre-calculated note colors with optimal text contrast (WCAG compliant)
// Colors calculated using 30° hue increments with 100% saturation, 75% lightness
// Text colors determined by luminance calculation for optimal contrast ratio (≥4.5:1)
export const NOTE_COLORS: Record<ScaleDegree | 'OTHER', NoteColor> = {
  'R': { // interval: 0, hue: getHueFromInterval(0) = 0
    bg: 'hsl(0, 100%, 75%)',
    border: 'hsl(0, 100%, 60%)',
    text: '#000000',
    stroke: '#FFFFFF'
  },
  'm2': { // interval: 1, hue: getHueFromInterval(1) = 30
    bg: 'hsl(30, 100%, 75%)',
    border: 'hsl(30, 100%, 60%)',
    text: '#000000',
    stroke: '#FFFFFF'
  },
  'M2': { // interval: 2, hue: getHueFromInterval(2) = 60
    bg: 'hsl(60, 100%, 75%)',
    border: 'hsl(60, 100%, 60%)',
    text: '#000000',
    stroke: '#FFFFFF'
  },
  'm3': { // interval: 3, hue: getHueFromInterval(3) = 90
    bg: 'hsl(90, 100%, 75%)',
    border: 'hsl(90, 100%, 60%)',
    text: '#000000',
    stroke: '#FFFFFF'
  },
  'M3': { // interval: 4, hue: getHueFromInterval(4) = 120
    bg: 'hsl(120, 100%, 75%)',
    border: 'hsl(120, 100%, 60%)',
    text: '#000000',
    stroke: '#FFFFFF'
  },
  'P4': { // interval: 5, hue: getHueFromInterval(5) = 150
    bg: 'hsl(150, 100%, 75%)',
    border: 'hsl(150, 100%, 60%)',
    text: '#000000',
    stroke: '#FFFFFF'
  },
  'A4': { // interval: 6, hue: getHueFromInterval(6) = 180
    bg: 'hsl(180, 100%, 75%)',
    border: 'hsl(180, 100%, 60%)',
    text: '#000000',
    stroke: '#FFFFFF'
  },
  'd5': { // interval: 6, hue: getHueFromInterval(6) = 180 (same as A4)
    bg: 'hsl(180, 100%, 75%)',
    border: 'hsl(180, 100%, 60%)',
    text: '#000000',
    stroke: '#FFFFFF'
  },
  'P5': { // interval: 7, hue: getHueFromInterval(7) = 210
    bg: 'hsl(210, 100%, 75%)',
    border: 'hsl(210, 100%, 60%)',
    text: '#FFFFFF',
    stroke: '#000000'
  },
  'm6': { // interval: 8, hue: getHueFromInterval(8) = 240
    bg: 'hsl(240, 100%, 75%)',
    border: 'hsl(240, 100%, 60%)',
    text: '#FFFFFF',
    stroke: '#000000'
  },
  'M6': { // interval: 9, hue: getHueFromInterval(9) = 270
    bg: 'hsl(270, 100%, 75%)',
    border: 'hsl(270, 100%, 60%)',
    text: '#FFFFFF',
    stroke: '#000000'
  },
  'm7': { // interval: 10, hue: getHueFromInterval(10) = 300
    bg: 'hsl(300, 100%, 75%)',
    border: 'hsl(300, 100%, 60%)',
    text: '#FFFFFF',
    stroke: '#000000'
  },
  'M7': { // interval: 11, hue: getHueFromInterval(11) = 330
    bg: 'hsl(330, 100%, 75%)',
    border: 'hsl(330, 100%, 60%)',
    text: '#FFFFFF',
    stroke: '#000000'
  },
  'OTHER': { // neutral gray for non-scale notes
    bg: 'hsl(0,0%,85%)',
    border: 'hsl(0,0%,70%)',
    text: '#000000',
    stroke: '#FFFFFF'
  }
} as const;

// ================================================================================
// SCALE QUALITY ABBREVIATIONS
// ================================================================================

export const SCALE_QUALITY_ABBREVIATIONS: Record<Scale, string> = {
  // Diatonic Scales
  MAJOR: '',
  MINOR: 'm',
  HARMONIC_MINOR: 'hm',
  MELODIC_MINOR: 'mm',
  
  // Modal Scales
  DORIAN: 'dor',
  PHRYGIAN: 'phr',
  LYDIAN: 'lyd',
  MIXOLYDIAN: 'mix',
  LOCRIAN: 'loc',
  
  // Pentatonic Scales
  PENTATONIC_MAJOR: 'p',
  PENTATONIC_MINOR: 'pm',
  
  // Blues and Other Scales
  BLUES: 'b',
  CHROMATIC: 'chr',
  WHOLE_TONE: 'wt',
  OCTATONIC_HALF_WHOLE: 'oct1',
  OCTATONIC_WHOLE_HALF: 'oct2',
  
  // Arpeggio abbreviations
  MAJOR_TRIAD: 'maj',
  MINOR_TRIAD: 'min',
  DIMINISHED_TRIAD: 'dim',
  AUGMENTED_TRIAD: 'aug',
  DOMINANT_7TH: '7',
  MAJOR_7TH: 'maj7',
  MINOR_7TH: 'm7',
  MINOR_MAJOR_7TH: 'm(maj7)',
  DIMINISHED_7TH: 'dim7',
  HALF_DIMINISHED_7TH: 'm7b5',
  AUGMENTED_7TH: 'aug7',
  AUGMENTED_MAJOR_7TH: 'aug(maj7)'
} as const;

// ================================================================================
// DISPLAY ABBREVIATIONS FOR UI
// ================================================================================

/**
 * Human-readable display names for scales (used in UI components)
 * These are longer than the SCALE_QUALITY_ABBREVIATIONS for better readability
 */
export const SCALE_DISPLAY_NAMES: Record<Scale, string> = {
  // Diatonic Scales
  MAJOR: 'Major',
  MINOR: 'Minor',
  HARMONIC_MINOR: 'Harm Min',
  MELODIC_MINOR: 'Mel Min',
  
  // Modal Scales
  DORIAN: 'Dorian',
  PHRYGIAN: 'Phrygian',
  LYDIAN: 'Lydian',
  MIXOLYDIAN: 'Mixolydian',
  LOCRIAN: 'Locrian',
  
  // Pentatonic Scales
  PENTATONIC_MAJOR: 'Pent Maj',
  PENTATONIC_MINOR: 'Pent Min',
  
  // Blues and Other Scales
  BLUES: 'Blues',
  CHROMATIC: 'Chromatic',
  WHOLE_TONE: 'Whole Tone',
  OCTATONIC_HALF_WHOLE: 'Oct H-W',
  OCTATONIC_WHOLE_HALF: 'Oct W-H',
  
  // Chord/Arpeggio display names
  MAJOR_TRIAD: 'Maj Triad',
  MINOR_TRIAD: 'Min Triad',
  DIMINISHED_TRIAD: 'Dim Triad',
  AUGMENTED_TRIAD: 'Aug Triad',
  DOMINANT_7TH: 'Dom 7th',
  MAJOR_7TH: 'Maj 7th',
  MINOR_7TH: 'Min 7th',
  MINOR_MAJOR_7TH: 'mMaj7',
  DIMINISHED_7TH: 'Dim 7th',
  HALF_DIMINISHED_7TH: 'ø7',
  AUGMENTED_7TH: 'Aug 7th',
  AUGMENTED_MAJOR_7TH: 'Aug Maj7'
} as const;
